// import styleObjToString from '../helpers/styleObjToString'
import Attrs from '../models/Attrs'
import type { BaseBuilderVersion, Flow, FlowPage, StripeCheckout } from '../models/types'
import { generateElementSelectors } from '../styles/selectors'
import renderTemplate from '../helpers/templating'
import { getBaseBuilderVersion } from '../helpers/utils'

const CONTROLLER = 'components--stripe-checkout'
export default function renderStripeCheckout(
  component: StripeCheckout,
  attrs: Attrs,
  page: FlowPage,
  flow: Flow,
  userData?: { [key: string]: unknown }
): string {
  const builderVersion = getBaseBuilderVersion(flow.builder_version)

  attrs
    .addController(CONTROLLER)
    .addDOMClass('StripeCheckout')
    .addAction(
      `${CONTROLLER}:next->flow#next`,
      `${CONTROLLER}:update->flow#update`,
      `${CONTROLLER}:validate->flow#triggerFormValidation`,
      `${CONTROLLER}:actions->flow#dispatchAction`
    )
    .addClass(CONTROLLER, 'complete', 'CardComplete')

  if (component.allow_alternate_payments) {
  }
  if (!component.hide_cc_fields) {
  }

  const formAttrs = new Attrs(attrs.static_render)
    .addAction(`submit->${CONTROLLER}#processPayment`)
    .addTarget(CONTROLLER, 'paymentForm')
    .add('id', 'payment-form')

  const cardFields = !component.hide_cc_fields
    ? renderCardFields(component, page, attrs.static_render)
    : ``

  const walletFields = component.allow_alternate_payments
    ? renderWalletFields(component, attrs.static_render)
    : ``

  const payButton = !component.hide_cc_fields
    ? renderPayButton(component, page, attrs.static_render, builderVersion, userData)
    : ``

  const form = `<div ${formAttrs}>${cardFields}${walletFields}${payButton}</div>`
  const messages = renderMessagesContainer(attrs)

  return `<div ${attrs.toString()}>${form}${messages}</div>`
}

function renderCardFields(
  component: StripeCheckout,
  page: FlowPage,
  attrsStaticRender: boolean
): string {
  const baseId = `stripe-container-${component.id}`
  const attrs = new Attrs(attrsStaticRender)
    .addTarget(CONTROLLER, 'card')
    .add('id', baseId)
    .addDOMClass('payment-box')

  // if (!component.individual_fields) attrs.addDOMClass('payment-box')

  // .add(
  //   'style',
  //   styleObjToString({
  //     background: '#fff',
  //     '-webkit-box-shadow':
  //       '0 13px 27px -5px rgb(50 50 93 / 13%), 0 8px 16px -8px rgb(0 0 0 / 10%), 0 -6px 16px -6px rgb(0 0 0 / 3%)',
  //     'box-shadow':
  //       '0 13px 27px -5px rgb(50 50 93 / 13%), 0 8px 16px -8px rgb(0 0 0 / 10%), 0 -6px 16px -6px rgb(0 0 0 / 3%)',
  //     'border-radius': '6px',
  //     padding: '12px',
  //     margin: '10px 0',
  //   })
  // )

  if (component.individual_fields) {
    const numAttrs = new Attrs(attrs.static_render)
      .addTarget(CONTROLLER, 'cardNum')
      .addDOMClass('stripe-card-container')
      .add('id', `${baseId}-number`)
      .add('style', 'position:relative;')
    const expiryAttrs = new Attrs(attrs.static_render)
      .addTarget(CONTROLLER, 'cardExpiry')
      .addDOMClass('stripe-card-container')
      .add('id', `${baseId}-expiry`)
      .add('style', 'position:relative;')
    const cvcAttrs = new Attrs(attrs.static_render)
      .addTarget(CONTROLLER, 'cardCvc')
      .addDOMClass('stripe-card-container')
      .add('id', `${baseId}-cvc`)
      .add('style', 'position:relative;')
    const numEl = `<div ${numAttrs}></div>`
    const expiryEl = `<div ${expiryAttrs}></div>`
    const cvcEl = `<div ${cvcAttrs}></div>`

    return `<div ${attrs}>${numEl}${expiryEl}${cvcEl}</div>`
  }

  return `<div ${attrs}></div>`
}

function renderWalletFields(component: StripeCheckout, attrsStaticRender: boolean): string {
  const attrs = new Attrs(attrsStaticRender)
    .addTarget(CONTROLLER, 'wallet')
    .addDOMClass('payment-box-apple-google')
    .add('id', `stripe-container-${component.id}-altPaymentContainer`)
  return `<div ${attrs}></div>`
}

function renderPayButton(
  component: StripeCheckout,
  page: FlowPage,
  attrsStaticRender: boolean,
  builderVersion: BaseBuilderVersion,
  userData?: { [key: string]: unknown }
): string {
  const attrs = new Attrs(attrsStaticRender)
    .addTarget(CONTROLLER, 'button')
    .add('type', 'button')
    .addDOMClass(
      generateElementSelectors({
        elementType: 'StripeCheckoutButton',
        component,
        page,
        elementTags: ['Main'],
        builderVersion,
      })
    )
    .addAction(`click->${CONTROLLER}#processPayment`)
  if (component.page_reactive_classes) attrs.addReactiveElement()
  const iconAttrs = new Attrs(attrs.static_render).addIcon('fa', 'chevron-right')
  const payText = getPayButtonText(component, userData)
  const payTextAttrs = new Attrs(attrs.static_render)

  //* Add template controller if the text has {{
  if (component.pay_button_text && component.pay_button_text.includes('{{'))
    payTextAttrs
      .addController('template')
      .addValueIfExists('template', 'text', encodeURIComponent(component.pay_button_text || ''))
      .addValueIfExists('template', 'always-rerender', component.always_rerender)

  return `<button ${attrs}><span ${payTextAttrs}>${payText}</span><svg ${iconAttrs}></svg></button>`
}

function renderMessagesContainer(attrs: Attrs): string {
  const errAttrs = new Attrs(attrs.static_render)
    .addTarget(CONTROLLER, 'errorContainer')
    .addDOMClass('error-message')
  const errIcon = new Attrs(attrs.static_render).addIcon('fa', 'exclamation-triangle')
  const successAttrs = new Attrs(attrs.static_render)
    .addTarget(CONTROLLER, 'successContainer')
    .addDOMClass('success-message')
  const errors = `<div ${errAttrs} style="display:none;"><svg ${errIcon}></svg></div>`
  const successes = `<div ${successAttrs} style="display:none;"></div>`
  return `<div class="payment-messages">${errors}${successes}</div>`
}

function getPayButtonText(component: StripeCheckout, userData?: { [key: string]: unknown }) {
  let currencySymbol: string
  switch (component.currency) {
    case 'gbp': {
      currencySymbol = '£'
      break
    }
    case 'eur': {
      currencySymbol = '€'
      break
    }
    case 'usd':
    case 'cad':
    default: {
      currencySymbol = '$'
      break
    }
  }

  if (component.pay_button_text) {
    const payButtonAfterTemplate = renderTemplate(component.pay_button_text, userData) || ''
    return payButtonAfterTemplate
  } else if (component.action === 'collect_details') return `Save Card Details`
  return `Pay ${currencySymbol}${component.amount}`
}
